/**
 * ROICTrendChartWithToggle — ROIC drivers chart with Chart / Table view toggle.
 *
 * Self-contained single-Card component. Toggle buttons are inline in the header
 * alongside the title (no absolute positioning).
 *
 * Chart view: NOPAT + Invested Capital bars (left axis) + ROIC/WACC lines (right axis)
 * Table view: NOPAT, IC, ROIC, WACC, ROIC–WACC Spread per year, most-recent first
 */
import { useState } from 'react';
import {
  ComposedChart,
  Bar,
  Line,
  LineChart,
  BarChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { BarChart2, TableProperties } from 'lucide-react';
import { Card, CardHeader, CardBody } from '../../ui/Card';
import { FocalBadge } from './ChartCard';
import { formatFiscalYear, formatPercent, formatCurrencyValue } from '../utils/formatters';
import { FOCAL_COLOR, PEER_COLORS, METRIC_COLOR, gridProps, zeroLineProps } from './chartTheme';
import type { UnifiedFinancialRecord, RegionConfig } from '../../../types/financial';

interface Props {
  data: UnifiedFinancialRecord[];
  region?: RegionConfig;
  companyName?: string;
  competitorData?: Record<string, UnifiedFinancialRecord[]>;
}

export default function ROICTrendChartWithToggle({
  data,
  region,
  companyName,
  competitorData,
}: Props) {
  const isPeerMode =
    !!competitorData && Object.keys(competitorData).length > 0 && !!companyName;

  // All chart/table cards default to table; user can opt into chart view.
  const [view, setView] = useState<'chart' | 'table'>('table');

  // Multi-company ROIC trend overlay
  if (isPeerMode) {
    const allYears = new Set<number>();
    data.forEach((d) => allYears.add(d.year));
    Object.values(competitorData!).forEach((records) =>
      records.forEach((r) => allYears.add(r.year)),
    );

    const focalSeries = new Map<number, number | null>();
    data.forEach((d) => {
      focalSeries.set(d.year, d.roic != null ? d.roic * 100 : null);
    });

    const peerSeries: Record<string, Map<number, number | null>> = {};
    Object.entries(competitorData!).forEach(([name, records]) => {
      const m = new Map<number, number | null>();
      records.forEach((r) => {
        m.set(r.year, r.roic != null ? r.roic * 100 : null);
      });
      peerSeries[name] = m;
    });

    const sortedYears = [...allYears].sort((a, b) => a - b);
    const peerNames = Object.keys(peerSeries);
    const chartData = sortedYears.map((year) => {
      const row: Record<string, any> = { year: formatFiscalYear(year, region) };
      row[companyName!] = focalSeries.get(year) ?? null;
      peerNames.forEach((name) => {
        row[name] = peerSeries[name].get(year) ?? null;
      });
      return row;
    });

    if (chartData.length < 2) return null;

    const tableYearsDesc = [...sortedYears].sort((a, b) => b - a);

    return (
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-neutral-800">
                10-Year ROIC Trend — Multi-Company Comparison (%)
              </h3>
              <p className="text-xs text-neutral-500 mt-0.5">
                Return on Invested Capital trajectory for focal company and peers.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <FocalBadge name={companyName!} />
              <div className="flex items-center rounded-lg border border-neutral-200 bg-white p-0.5 shadow-sm">
                <button
                  onClick={() => setView('chart')}
                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                    view === 'chart'
                      ? 'bg-primary-600 text-white shadow-sm'
                      : 'text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100'
                  }`}
                >
                  <BarChart2 className="w-3.5 h-3.5" />
                  Chart
                </button>
                <button
                  onClick={() => setView('table')}
                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                    view === 'table'
                      ? 'bg-primary-600 text-white shadow-sm'
                      : 'text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100'
                  }`}
                >
                  <TableProperties className="w-3.5 h-3.5" />
                  Table
                </button>
              </div>
            </div>
          </div>
        </CardHeader>
        {view === 'chart' ? (
          <CardBody>
            <ResponsiveContainer width="100%" height={360}>
              <BarChart
                data={chartData}
                margin={{ top: 20, right: 16, left: 8, bottom: 8 }}
                barCategoryGap="18%"
                barGap={2}
              >
                <CartesianGrid {...gridProps} />
                <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                <YAxis
                  width={48}
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) => `${v?.toFixed(0)}%`}
                />
                <ReferenceLine y={0} {...zeroLineProps} />
                <Tooltip
                  formatter={(v: number, name: string) => [`${v?.toFixed(1)}%`, name]}
                  itemSorter={(item: any) => -item.value}
                  wrapperStyle={{ fontSize: 11 }}
                  cursor={{ fill: '#f9fafb' }}
                />
                <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} iconType="square" />
                {/* Focal first so it appears leftmost in each year group */}
                <Bar
                  dataKey={companyName!}
                  fill={FOCAL_COLOR}
                  radius={[3, 3, 0, 0]}
                />
                {peerNames.map((name, idx) => (
                  <Bar
                    key={name}
                    dataKey={name}
                    fill={PEER_COLORS[idx % PEER_COLORS.length]}
                    radius={[3, 3, 0, 0]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </CardBody>
        ) : (
          <CardBody className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b-2 border-neutral-200 bg-neutral-50">
                    <th className="text-left py-3 px-4 text-neutral-500 font-semibold uppercase tracking-wider sticky left-0 bg-neutral-50 z-10">
                      Year
                    </th>
                    <th className="text-right py-3 px-4 text-secondary-800 font-bold uppercase tracking-wider whitespace-nowrap">
                      ★ {companyName}
                    </th>
                    {peerNames.map((name) => (
                      <th
                        key={name}
                        className="text-right py-3 px-4 text-neutral-600 font-semibold uppercase tracking-wider whitespace-nowrap"
                      >
                        {name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableYearsDesc.map((year, rowIdx) => {
                    const focalVal = focalSeries.get(year);
                    return (
                      <tr
                        key={year}
                        className={`border-b border-neutral-100 hover:bg-blue-50/30 ${
                          rowIdx === 0 ? 'bg-blue-50/20' : ''
                        }`}
                      >
                        <td className="py-3 px-4 font-semibold text-neutral-800 tabular-nums sticky left-0 bg-white">
                          {formatFiscalYear(year, region)}
                          {rowIdx === 0 && (
                            <span className="ml-1.5 text-[9px] bg-primary-100 text-primary-700 px-1.5 py-0.5 rounded-full font-bold">
                              Latest
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-right tabular-nums font-semibold text-secondary-800">
                          {focalVal != null ? `${focalVal.toFixed(1)}%` : '—'}
                        </td>
                        {peerNames.map((name) => {
                          const v = peerSeries[name].get(year);
                          return (
                            <td
                              key={name}
                              className="py-3 px-4 text-right tabular-nums text-neutral-700"
                            >
                              {v != null ? `${v.toFixed(1)}%` : '—'}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardBody>
        )}
      </Card>
    );
  }

  const unit = region?.currency?.unit_abbr ?? '';

  const chartData = data.map((d) => ({
    year: formatFiscalYear(d.year, region),
    nopat: d.nopat,
    investedCapital: d.invested_capital,
    roic: d.roic != null ? d.roic * 100 : null,
    wacc: d.wacc != null ? d.wacc * 100 : null,
  }));

  const tableRows = [...data].reverse();

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <div>
            <h3 className="text-sm font-semibold text-neutral-800">ROIC Drivers ({unit})</h3>
            <p className="text-xs text-neutral-500 mt-0.5">NOPAT / Invested Capital</p>
          </div>
          {/* Inline toggle — no absolute positioning */}
          <div className="flex items-center rounded-lg border border-neutral-200 bg-white p-0.5 shadow-sm shrink-0">
            <button
              onClick={() => setView('chart')}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                view === 'chart'
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100'
              }`}
            >
              <BarChart2 className="w-3.5 h-3.5" />
              Chart
            </button>
            <button
              onClick={() => setView('table')}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                view === 'table'
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100'
              }`}
            >
              <TableProperties className="w-3.5 h-3.5" />
              Table
            </button>
          </div>
        </div>
      </CardHeader>

      {view === 'chart' ? (
        <CardBody>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={chartData} margin={{ top: 20, right: 40, left: 8, bottom: 0 }}>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="year" tick={{ fontSize: 12 }} />
              <YAxis
                yAxisId="left"
                width={80}
                domain={['auto', 'auto']}
                tick={{ fontSize: 12 }}
                tickFormatter={(v) => formatCurrencyValue(v, region, true)}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fontSize: 12 }}
                tickFormatter={(v) => `${v?.toFixed(1)}%`}
              />
              <Tooltip
                formatter={(value: number, name: string) => {
                  if (name === 'ROIC' || name === 'WACC')
                    return [`${value?.toFixed(1)}%`, name];
                  return [formatCurrencyValue(value, region), name];
                }}
              />
              <Legend />
              <Bar
                yAxisId="left"
                dataKey="nopat"
                name="NOPAT"
                fill={METRIC_COLOR.nopat}
                radius={[3, 3, 0, 0]}
              />
              <Bar
                yAxisId="left"
                dataKey="investedCapital"
                name="Invested Capital"
                fill={METRIC_COLOR.invested_capital}
                radius={[3, 3, 0, 0]}
              />
              <ReferenceLine yAxisId="right" y={0} {...zeroLineProps} />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="roic"
                name="ROIC"
                stroke={METRIC_COLOR.roic}
                dot={{ r: 3 }}
                strokeWidth={2}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="wacc"
                name="WACC"
                stroke={METRIC_COLOR.wacc}
                dot={{ r: 3 }}
                strokeWidth={2}
                strokeDasharray="4 2"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </CardBody>
      ) : (
        <CardBody className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b-2 border-neutral-200 bg-neutral-50">
                  <th className="text-left py-3 px-4 text-neutral-500 font-semibold uppercase tracking-wider w-24">
                    Year
                  </th>
                  <th className="text-right py-3 px-4 text-neutral-500 font-semibold uppercase tracking-wider">
                    NOPAT
                  </th>
                  <th className="text-right py-3 px-4 text-neutral-500 font-semibold uppercase tracking-wider">
                    Invested Capital
                  </th>
                  <th className="text-right py-3 px-4 text-neutral-500 font-semibold uppercase tracking-wider">
                    ROIC
                  </th>
                  <th className="text-right py-3 px-4 text-neutral-500 font-semibold uppercase tracking-wider">
                    WACC
                  </th>
                  <th className="text-right py-3 px-4 text-neutral-500 font-semibold uppercase tracking-wider">
                    ROIC–WACC Spread
                  </th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((d, i) => {
                  const spread =
                    d.roic != null && d.wacc != null ? (d.roic - d.wacc) * 100 : null;
                  return (
                    <tr
                      key={d.year}
                      className={`border-b border-neutral-100 hover:bg-blue-50/30 transition-colors ${
                        i === 0 ? 'bg-blue-50/20' : ''
                      }`}
                    >
                      <td className="py-3 px-4 font-semibold text-neutral-800 tabular-nums">
                        {formatFiscalYear(d.year, region)}
                        {i === 0 && (
                          <span className="ml-1.5 text-[9px] bg-primary-100 text-primary-700 px-1.5 py-0.5 rounded-full font-bold">
                            Latest
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right tabular-nums text-emerald-700 whitespace-nowrap">
                        {d.nopat != null ? formatCurrencyValue(d.nopat, region) : '—'}
                      </td>
                      <td className="py-3 px-4 text-right tabular-nums text-neutral-700 whitespace-nowrap">
                        {d.invested_capital != null
                          ? formatCurrencyValue(d.invested_capital, region)
                          : '—'}
                      </td>
                      <td className="py-3 px-4 text-right tabular-nums font-semibold text-blue-700">
                        {d.roic != null ? formatPercent(d.roic) : '—'}
                      </td>
                      <td className="py-3 px-4 text-right tabular-nums text-red-600">
                        {d.wacc != null ? formatPercent(d.wacc) : '—'}
                      </td>
                      <td
                        className={`py-3 px-4 text-right tabular-nums font-semibold ${
                          spread != null
                            ? spread >= 0
                              ? 'text-emerald-700'
                              : 'text-red-600'
                            : 'text-neutral-400'
                        }`}
                      >
                        {spread != null
                          ? `${spread >= 0 ? '+' : ''}${spread.toFixed(1)}pp`
                          : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardBody>
      )}
    </Card>
  );
}
